import { AxiosResponse } from 'axios';
import axiosInstance from '../../../axiosInstance';
import { setShowAlert } from '../../utilts/utilsSlice';
import { TOAST_TYPE } from '../../../utils/constantTypes';
import {
  CreateLearningResourcePayload,
  DeleteLearningResourcePayload,
  FetchLearningResourcesPayload,
  LearningResourceApiResponse,
  UpdateLearningResourcePayload,
} from './types';

type ApiDispatch = {
  dispatch: (action: ReturnType<typeof setShowAlert>) => void;
};

const authHeaders = (accessToken: string) => ({
  Authorization: `Bearer ${accessToken}`,
});

const apiErrorMessage = (err: {
  response?: { data?: { msg?: string } };
  message?: string;
}) => err?.response?.data?.msg || err?.message || 'Something went wrong';

const withApi = async <T extends LearningResourceApiResponse>(
  request: () => Promise<AxiosResponse<T>>,
  { dispatch }: ApiDispatch,
): Promise<T> => {
  try {
    const response = await request();
    return response.data;
  } catch (err) {
    const message = apiErrorMessage(err);
    dispatch(
      setShowAlert({
        show: true,
        message,
        type: TOAST_TYPE.ERROR,
      }),
    );
    throw new Error(message);
  }
};

export const getLearningResources = (
  payload: FetchLearningResourcesPayload,
  ctx: ApiDispatch,
) => {
  const params: Record<string, string | number> = {};
  if (payload.category) {
    params.category = payload.category;
  } else if (payload.categories?.length) {
    params.categories = payload.categories.join(',');
  }
  if (payload.offset !== undefined) {
    params.offset = payload.offset;
  }
  if (payload.limit !== undefined) {
    params.limit = payload.limit;
  }
  if (payload.sortBy) {
    params.sortBy = payload.sortBy;
  }
  if (payload.sortOrder) {
    params.sortOrder = payload.sortOrder;
  }
  if (payload.includeInactive) {
    params.includeInactive = 'true';
  }
  return withApi(
    () =>
      axiosInstance.get('/learning-resources', {
        headers: authHeaders(payload.accessToken),
        params,
      }),
    ctx,
  );
};

export const createLearningResource = (
  payload: CreateLearningResourcePayload,
  ctx: ApiDispatch,
) => {
  const { accessToken, ...body } = payload;
  return withApi(
    () =>
      axiosInstance.post('/learning-resources', body, {
        headers: authHeaders(accessToken),
      }),
    ctx,
  );
};

export const updateLearningResource = (
  payload: UpdateLearningResourcePayload,
  ctx: ApiDispatch,
) => {
  const { accessToken, id, ...body } = payload;
  return withApi(
    () =>
      axiosInstance.patch(`/learning-resources/${id}`, body, {
        headers: authHeaders(accessToken),
      }),
    ctx,
  );
};

export const deleteLearningResource = (
  payload: DeleteLearningResourcePayload,
  ctx: ApiDispatch,
) => {
  const { accessToken, id } = payload;
  return withApi(
    () =>
      axiosInstance.delete(`/learning-resources/${id}`, {
        headers: authHeaders(accessToken),
      }),
    ctx,
  );
};
